import React, { useEffect, useMemo, useRef, useState } from 'react'
import parse from 'html-react-parser'
import { PlayIcon } from './Icons'

const SECTION_LABELS = {
  habitat: 'Habitat',
  behavior: 'Behaviours',
}

// Image component with thumbnail placeholder
function ObservationImage({ mediaId, alt }) {
  const [imageLoaded, setImageLoaded] = useState(false)
  
  return (
    <div className="observations-tab__media-image">
      {/* Blurred thumbnail placeholder */}
      <img 
        src={`assets/expeditions-data/thumbnails/${encodeURIComponent(mediaId)}`}
        alt=""
        className={`observations-tab__placeholder ${imageLoaded ? '-hidden' : ''}`}
        aria-hidden="true"
      />
      {/* Full-size image */}
      <img
        src={`assets/expeditions-data/images/${encodeURIComponent(mediaId)}`}
        alt={alt}
        className={`observations-tab__full-image ${imageLoaded ? '-loaded' : ''}`}
        onLoad={() => setImageLoaded(true)}
      />
    </div>
  )
}

// Video component with lazy-load (thumbnail until clicked)
function ObservationVideo({ videoId, title }) {
  const [isPlaying, setIsPlaying] = useState(false)
  
  if (isPlaying) {
    return (
      <div className="observations-tab__media-video">
        <iframe
          src={`https://player.vimeo.com/video/${videoId}?autoplay=1`}
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          title={title}
        />
      </div>
    )
  }
  
  return (
    <div 
      className="observations-tab__media-video -thumbnail"
      onClick={() => setIsPlaying(true)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          setIsPlaying(true)
        }
      }}
    >
      <img 
        src={`https://vumbnail.com/${videoId}.jpg`}
        alt={title}
        onError={(e) => {
          e.target.style.display = 'none'
        }}
      />
      <div className="observations-tab__play-icon">
        <PlayIcon />
      </div>
    </div>
  )
}

function getFirstSelection(observations) {
  if (!observations) return null

  if (observations.habitat && observations.habitat.length > 0) {
    return {
      section: 'habitat',
      index: 0,
      item: observations.habitat[0],
    }
  }

  if (observations.behavior && observations.behavior.length > 0) {
    return {
      section: 'behavior',
      index: 0,
      item: observations.behavior[0],
    }
  }

  return null
}

export function ObservationsTab({ observations }) {
  const [selected, setSelected] = useState(() => getFirstSelection(observations))
  const mainRef = useRef(null)

  const handleSelect = (selection) => {
    setSelected(selection)
    mainRef.current?.scrollTo({ top: 0 })
  }

  useEffect(() => {
    setSelected(getFirstSelection(observations))
  }, [observations])

  const sections = useMemo(() => ({
    habitat: observations?.habitat || [],
    behavior: observations?.behavior || [],
  }), [observations])

  if (!observations) {
    return (
      <div className="data-explorer__placeholder">
        <p>No observations available</p>
      </div>
    )
  }

  return (
    <>
      <aside className="data-explorer__sidebar observations-tab__sidebar">
        <div className="observations-tab__sidebar-content">
          {Object.entries(sections).map(([sectionKey, items]) => (
            <div className="observations-tab__section" key={sectionKey}>
              <h3 className="observations-tab__section-title">
                {SECTION_LABELS[sectionKey] || sectionKey}
              </h3>
              <div className="observations-tab__items">
                {items.map((item, index) => {
                  const isActive = selected?.section === sectionKey && selected?.index === index
                  return (
                    <span
                      key={`${sectionKey}-${index}`}
                      role="button"
                      tabIndex={0}
                      className={`observations-tab__item ${isActive ? '-active' : ''}`}
                      onClick={() => handleSelect({ section: sectionKey, index, item })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          handleSelect({ section: sectionKey, index, item })
                        }
                      }}
                    >
                      {item.title}
                    </span>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </aside>

      <div className="data-explorer__main observations-tab__main" ref={mainRef}>
        {selected ? (
          <div 
            className="observations-tab__content"
            key={`${selected.section}-${selected.index}`}
          >
            <h4 className="observations-tab__title">{selected.item.title}</h4>
            <div className="observations-tab__text">
              {(selected.item.text || []).map((block, index) => {
                if (block.type !== 'text') return null
                return <p key={`text-${index}`}>{parse(block.value)}</p>
              })}
            </div>
            {selected.item.media && selected.item.media.length > 0 && (
              <div className="observations-tab__media">
                {selected.item.media.map((media, index) => {
                  if (media.type === 'video') {
                    return (
                      <ObservationVideo 
                        key={`media-${index}`}
                        videoId={media.id}
                        title={`${selected.item.title} video ${index + 1}`}
                      />
                    )
                  }

                  return (
                    <ObservationImage 
                      key={`media-${index}`}
                      mediaId={media.id}
                      alt={selected.item.title}
                    />
                  )
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="data-explorer__placeholder">
            <p>Select an observation</p>
          </div>
        )}
      </div>
    </>
  )
}
